import React from "react";

import firebase from "firebase/compat/app";
import "firebase/compat/analytics";

import {
  Box,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Clear as ClearIcon } from "@mui/icons-material";
import { useNavigate, Routes, Route } from "react-router-dom";
import { useConfirm } from "material-ui-confirm";
import { useRecoilValue } from "recoil";
import {
  usePaginatedExercisesBigQuery,
  useDeleteCustomExerciseBigQuery,
} from "../../../hooks/useExercisesBigQuery";
import {
  useMuscleGroupsBigQuery,
  useEquipmentBigQuery,
} from "../../../hooks/useStaticDataBigQuery";
import MuscleGroupButtonGroup from "./exercises/MuscleGroupButtonGroup";
import ExerciseDetails from "./exercises/ExerciseDetails";
import ExerciseDataGrid from "./exercises/ExerciseDataGrid";
import ExerciseDialog from "./exercises/ExerciseDialog";

import { exerciseCache } from "../../../utils/ExerciseCache";

import organizationIdSelector from "../../../atoms/organizationIdSelector";
import useEvents from "../../../hooks/useEvents";

import PageHeader from "../../../components/PageHeader";
import ExerciseCacheStats from "../../../components/exercises/ExerciseCacheStats";
import StandardButton from "../../../components/StandardButton";
import { useRenderTracker } from "../../../hooks/useWhyDidYouUpdate";

// ExercisesList Component - List view of exercises with pagination
const ExercisesList = () => {
  console.log("Rendering ExercisesList");
  const organizationId = useRecoilValue(organizationIdSelector);
  // const { getAllProtocols } = useProtocols();

  // BigQuery hooks for static data
  const { data: muscleGroupsData } = useMuscleGroupsBigQuery();
  const { data: equipmentData } = useEquipmentBigQuery();

  const confirm = useConfirm();
  const eventsHook = useEvents();

  // Create stable event handlers using useCallback at the top level
  const logButtonClick = React.useCallback(
    (event) => eventsHook.logButtonClick(event),
    [],
  );
  const logMenuComponent = React.useCallback(
    (event) => eventsHook.logMenuComponent(event),
    [],
  );
  const logHyperlink = React.useCallback(
    (event) => eventsHook.logHyperlink(event),
    [],
  );
  const logSignout = React.useCallback(
    (event) => eventsHook.logSignout(event),
    [],
  );
  const logTabSwitch = React.useCallback(
    (event) => eventsHook.logTabSwitch(event),
    [],
  );
  const logTextInput = React.useCallback(
    (event) => eventsHook.logTextInput(event),
    [],
  );
  const logSubpageAccess = React.useCallback(
    (event) => eventsHook.logSubpageAccess(event),
    [],
  );
  const logMessage = React.useCallback(
    (event) => eventsHook.logMessage(event),
    [],
  );
  const logDayClick = React.useCallback(
    (event) => eventsHook.logDayClick(event),
    [],
  );

  // Combine into stable events object
  const events = React.useMemo(
    () => ({
      logButtonClick,
      logMenuComponent,
      logHyperlink,
      logSignout,
      logTabSwitch,
      logTextInput,
      logSubpageAccess,
      logMessage,
      logDayClick,
    }),
    [
      logButtonClick,
      logMenuComponent,
      logHyperlink,
      logSignout,
      logTabSwitch,
      logTextInput,
      logSubpageAccess,
      logMessage,
      logDayClick,
    ],
  );

  const navigate = useNavigate();
  // customSubscription removed - React Query handles real-time updates

  const [muscleGroups, setMuscleGroups] = React.useState([]);

  // Get stored values from localStorage using useState initializer to avoid re-computation
  const [storedSearch] = React.useState(
    () => localStorage.getItem("exerciseSearch") || "",
  );
  const [storedFilter] = React.useState(
    () => localStorage.getItem("exerciseFilter") || "all",
  );
  // TODO: BigQuery API doesn't support these filters yet - implement in API if needed
  // const [storedShowCustom] = React.useState(() => localStorage.getItem("showCustomOnly") === "true");
  // const [storedShowDeleted] = React.useState(() => localStorage.getItem("showDeleted") === "true");

  const [search, setSearch] = React.useState(storedSearch);
  const [searchInputValue, setSearchInputValue] = React.useState(storedSearch);

  // Developer debugging flag - only show performance info if explicitly enabled
  const showPerformanceInfo =
    localStorage.getItem("showExercisePerformanceInfo") === "true";

  // TODO: Implement these filters in BigQuery API if needed
  // const [showCustomOnly, setShowCustomOnly] = React.useState(
  //   storedShowCustom || false,
  // );
  // const [showDeleted, setShowDeleted] = React.useState(
  //   storedShowDeleted || false,
  // );

  // Always enable pagination for better performance - now handled by BigQuery
  // const enablePagination = true; // Removed as BigQuery always uses pagination

  const [muscleGroupsKey, setMuscleGroupsKey] = React.useState({});
  const [equipmentKey, setEquipmentKey] = React.useState({});
  const [filter, setFilter] = React.useState(storedFilter);

  // const [allProtocols, setAllProtocols] = React.useState([]);

  const [exerciseDialogOpen, setExerciseDialogOpen] = React.useState(false);
  const selectedExercise = React.useRef(null);

  const textInputRef = React.useRef(null);
  const searchTimeout = React.useRef();

  // Pagination state managed locally
  const [currentPage, setCurrentPage] = React.useState(0);
  const pageSize = 25;
  
  // Manual delete loading state to ensure overlay shows during entire operation
  const [isDeleting, setIsDeleting] = React.useState(false);

  // BigQuery mutations for exercises (only keep delete since dialog handles create/update)
  const deleteExercise = useDeleteCustomExerciseBigQuery();

  // Memoize data to prevent unnecessary re-renders caused by reference changes
  // Use deep comparison on actual data arrays to prevent infinite loops
  const memoizedEquipmentData = React.useMemo(() => {
    if (!equipmentData || !Array.isArray(equipmentData)) return [];
    return equipmentData;
  }, [JSON.stringify(equipmentData)]); // Deep comparison using JSON

  const memoizedMuscleGroupsData = React.useMemo(() => {
    if (!muscleGroupsData || !Array.isArray(muscleGroupsData)) return [];
    return muscleGroupsData;
  }, [JSON.stringify(muscleGroupsData)]); // Deep comparison using JSON

  // Memoize the deleteExercise mutation to prevent re-renders
  // Only depend on the actual mutation function, not the whole object
  const memoizedDeleteExercise = React.useMemo(() => {
    return {
      mutate: deleteExercise.mutate,
      mutateAsync: deleteExercise.mutateAsync,
      isLoading: deleteExercise.isLoading,
    };
  }, [
    deleteExercise.mutate,
    deleteExercise.mutateAsync,
    deleteExercise.isLoading,
  ]);

  // Add render tracking to debug infinite re-renders - exclude unstable props
  useRenderTracker("ExercisesList", {
    organizationId,
    muscleGroups,
    search,
    searchInputValue,
    filter,
    currentPage,
    exerciseDialogOpen,
    events,
    // Removed: equipmentData, deleteExercise (unstable references)
    // Removed: confirm, navigate (these are from external hooks)
  });

  // Debug effect to track when the hook should run
  React.useEffect(() => {
    console.log("🟠 ExercisesPage useEffect - Hook dependencies changed:", {
      filter,
      search,
      pageSize,
      currentPage,
    });
  }, [filter, search, pageSize, currentPage]);

  // Use the new BigQuery paginated exercises hook
  console.log("🔴 About to call usePaginatedExercisesBigQuery with:", {
    filter,
    search,
    pageSize,
    currentPage,
  });
  const { exercises, loading, totalRows, refetch } =
    usePaginatedExercisesBigQuery({
      filter: filter,
      search,
      pageSize,
      currentPage,
      enabled: true,
    });
  console.log("🔴 usePaginatedExercisesBigQuery returned:", {
    exercises,
    loading,
    totalRows,
  });
  console.log("🔄 Delete mutation loading state:", {
    deleteIsLoading: memoizedDeleteExercise.isLoading,
    manualDeleteLoading: isDeleting,
    combinedLoading: loading || memoizedDeleteExercise.isLoading || isDeleting,
  });

  // Maintain previous totalRows during loading to prevent DataGrid pagination reset
  const [stableTotalRows, setStableTotalRows] = React.useState(0);
  React.useEffect(() => {
    if (!loading && totalRows !== undefined) {
      setStableTotalRows(totalRows);
    }
  }, [loading, totalRows]);

  // Use stable totalRows to prevent DataGrid from resetting pagination during loading
  const displayTotalRows = loading ? stableTotalRows : totalRows;

  // Pagination handlers
  const handlePageChange = React.useCallback(
    (newPage) => {
      console.log("📄 Page change requested:", newPage);

      // For server-side pagination, don't validate against totalRows during loading
      // Just set the page and let the server handle validation
      if (loading) {
        console.log(
          "📄 Loading state - setting page without validation:",
          newPage,
        );
        setCurrentPage(newPage);
        return;
      }

      // Only validate when we have actual data (not loading)
      const maxPage = Math.max(
        0,
        Math.ceil((displayTotalRows || 0) / pageSize) - 1,
      );
      const validPage = Math.max(0, Math.min(newPage, maxPage));

      console.log("📄 Valid page calculated:", validPage, "max page:", maxPage);
      setCurrentPage(validPage);
    },
    [displayTotalRows, pageSize, loading],
  );

  const handlePageSizeChange = React.useCallback((newPageSize) => {
    console.log("📏 Page size change requested:", newPageSize);
    setCurrentPage(0); // Reset to first page when changing page size
    // Note: For server-side pagination, page size is controlled by BigQuery hook
    // This is mainly for DataGrid UI state management
  }, []);

  // Memoize the muscle group filter click handler to prevent infinite re-renders
  const handleMuscleGroupClick = React.useCallback(
    (id) => {
      console.log("MuscleGroupButtonGroup clicked", id);
      setFilter(id);
      events.logButtonClick({
        buttonName: "Filtered Muscle Group",
        pageName: "Exercises",
      });
    },
    [], // Remove events from dependencies since it's not stable
  );

  // Performance info for compatibility - memoize to prevent unnecessary re-renders
  const performanceInfo = React.useMemo(
    () => ({
      totalExercises: displayTotalRows,
      filteredExercises: displayTotalRows,
      displayedExercises: exercises?.length || 0,
      currentPage: currentPage + 1,
      totalPages: Math.ceil((displayTotalRows || 0) / pageSize),
      pageSize,
      cacheStats: { hits: 0, misses: 0 }, // Placeholder for compatibility
    }),
    [displayTotalRows, exercises?.length, currentPage, pageSize],
  );

  React.useEffect(() => {
    firebase.analytics().logEvent("page_view", {
      page_title: "Exercises Page",
      page_location: "/exercises",
    });
  }, []);

  // Persist search state to localStorage
  React.useEffect(() => {
    localStorage.setItem("exerciseSearch", search);
  }, [search]);

  // Persist filter state to localStorage
  React.useEffect(() => {
    localStorage.setItem("exerciseFilter", filter);
  }, [filter]);

  // Reset to first page when search or filter changes
  React.useEffect(() => {
    console.log("🔄 Search or filter changed, resetting to first page");
    setCurrentPage(0);
  }, [search, filter]);

  // TODO: Re-enable when BigQuery API supports these filters
  // Persist showCustomOnly state to localStorage (already handled, but keeping for consistency)
  // React.useEffect(() => {
  //   localStorage.setItem("showCustomOnly", showCustomOnly.toString());
  // }, [showCustomOnly]);

  // Persist showDeleted state to localStorage (already handled, but keeping for consistency)
  // React.useEffect(() => {
  //   localStorage.setItem("showDeleted", showDeleted.toString());
  // }, [showDeleted]);

  // Set initial value of search input to match stored search
  React.useEffect(() => {
    if (textInputRef.current && storedSearch) {
      textInputRef.current.value = storedSearch;
    }
  }, [storedSearch]);

  // Handle BigQuery static data updates
  React.useEffect(() => {
    if (memoizedMuscleGroupsData && Array.isArray(memoizedMuscleGroupsData)) {
      setMuscleGroups(memoizedMuscleGroupsData);

      const groups = {};
      memoizedMuscleGroupsData.forEach((g) => {
        // Handle both Firebase format (with .data() method) and direct object format
        const data = typeof g.data === "function" ? g.data() : g;
        if (data && data.id && data.name) {
          groups[data.id] = data.name;
        }
      });
      setMuscleGroupsKey(groups);
    }
  }, [memoizedMuscleGroupsData]);

  React.useEffect(() => {
    if (memoizedEquipmentData && Array.isArray(memoizedEquipmentData)) {
      const equipmentKey = {};
      memoizedEquipmentData.forEach((e) => {
        // Handle both Firebase format (with .data() method) and direct object format
        const data = typeof e.data === "function" ? e.data() : e;
        if (data && data.id && data.name) {
          equipmentKey[data.id] = data.name;
        }
      });
      setEquipmentKey(equipmentKey);
    }
  }, [memoizedEquipmentData]);

  React.useEffect(() => {
    async function getData() {
      // Load protocols once - removed Firebase calls, now using BigQuery hooks above
      // const protocols = await getAllProtocols({ organizationId });
      // console.log("Loaded protocols:", protocols.length);
      // setAllProtocols(protocols);
      // Static data now loaded via BigQuery hooks above
    }
    getData();
  }, [organizationId]);

  // TODO: Re-enable when BigQuery API supports these filters
  // React.useEffect(() => {
  //   localStorage.setItem("showCustomOnly", showCustomOnly);
  //   localStorage.setItem("showDeleted", showDeleted);
  // }, [showCustomOnly, showDeleted]);

  // Custom exercise subscription removed - React Query handles real-time updates via cache invalidation

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Cache preloading is now handled by BigQuery exercise system
  // No manual preload needed with React Query caching

  return (
    <>
      <PageHeader 
        title="Exercises" 
        subtitle="Browse and manage your exercise library and movement database"
      />
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Search and Filter Controls */}
        <Box sx={{ mb: 1.5, mt: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.75,
              flexWrap: "wrap",
            }}
          >
            <TextField
              inputRef={textInputRef}
              placeholder="Search by exercise name"
              variant="outlined"
              size="small"
              sx={{
                minWidth: { xs: "100%", md: 320 },
                width: { xs: "100%", md: "45%" },
                maxWidth: 560,
                flex: { xs: "1 1 100%", md: "1 1 420px" },
              }}
              value={searchInputValue}
              onChange={(event) => {
                const value = event.target.value;
                setSearchInputValue(value); // Update local state immediately

                if (searchTimeout.current) {
                  clearTimeout(searchTimeout.current);
                }
                searchTimeout.current = setTimeout(() => {
                  setSearch(value); // Update search state with delay
                  events.logTextInput({
                    pageName: "Exercises",
                    textboxName: "Search Exercises",
                    text: value,
                  });
                }, 200); // Reduced from 500ms to 200ms for better responsiveness
              }}
              InputProps={{
                endAdornment: searchInputValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearch("");
                        setSearchInputValue("");
                        textInputRef.current.value = "";
                        localStorage.setItem("exerciseSearch", "");
                        events.logButtonClick({
                          buttonName: "Clear Search",
                          pageName: "Exercises",
                        });
                      }}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <StandardButton
              variant="primary"
              onClick={() => {
                selectedExercise.current = null;
                setExerciseDialogOpen(true);
                events.logButtonClick({
                  buttonName: "Add New Exercise",
                  pageName: "Exercises",
                });
              }}
            >
              Add New Exercise
            </StandardButton>
            <StandardButton
              variant="secondary"
              size="small"
              onClick={() => {
                setSearch("");
                setSearchInputValue("");
                textInputRef.current.value = "";
                // TODO: Re-enable when BigQuery API supports these filters
                // setShowCustomOnly(false);
                // setShowDeleted(false);
                setFilter("all"); // Reset to full body filter

                localStorage.removeItem("exerciseSearch");
                localStorage.removeItem("exerciseFilter");
                // TODO: Re-enable when BigQuery API supports these filters
                // localStorage.removeItem("showCustomOnly");
                // localStorage.removeItem("showDeleted");
                events.logButtonClick({
                  buttonName: "Clear Search",
                  pageName: "Exercises",
                });
              }}
            >
              Clear All Filters
            </StandardButton>
            <StandardButton
              variant="secondary"
              size="small"
              onClick={async () => {
                console.log("🔄 Manual refresh requested");

                // Clear any pending search timeouts to prevent interference
                if (searchTimeout.current) {
                  clearTimeout(searchTimeout.current);
                  console.log(
                    "🔄 Cleared pending search timeout for manual refresh",
                  );
                }

                try {
                  await refetch();
                  events.logButtonClick({
                    buttonName: "Refresh Exercises",
                    pageName: "Exercises",
                  });
                  console.log("✅ Exercise data refreshed successfully");
                } catch (error) {
                  console.error("❌ Error refreshing exercises:", error);
                }
              }}
            >
              Refresh
            </StandardButton>
          </Box>

          {/* Performance Info */}
          {showPerformanceInfo && performanceInfo && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Performance: Showing {performanceInfo.displayedExercises} of{" "}
                {performanceInfo.filteredExercises} exercises (Page{" "}
                {performanceInfo.currentPage} of {performanceInfo.totalPages})
                {performanceInfo.filteredExercises <
                  performanceInfo.totalExercises &&
                  ` • ${performanceInfo.totalExercises - performanceInfo.filteredExercises} filtered out`}
              </Typography>
            </Box>
          )}
          {showPerformanceInfo && (
            <Box sx={{ mt: 0.5 }}>
              <ExerciseCacheStats performanceInfo={performanceInfo} />
            </Box>
          )}
        </Box>

        <MuscleGroupButtonGroup
          muscleGroups={muscleGroups}
          filter={filter}
          onClick={handleMuscleGroupClick}
        />

        {/* Show notice when search overrides muscle group filter */}
        {searchInputValue && searchInputValue.length > 0 && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontStyle: "italic",
                bgcolor: "warning.light",
                color: "warning.contrastText",
                p: 1.5,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "warning.main",
              }}
            >
              🔍 Search is active - showing results across all muscle groups
              (muscle group filter temporarily disabled)
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            width: "100%",
            overflow: "hidden",
          }}
        >
          <ExerciseDataGrid
            exercises={exercises}
            muscleGroupsKey={muscleGroupsKey}
            equipmentKey={equipmentKey}
            // allProtocols={allProtocols}
            loading={loading || memoizedDeleteExercise.isLoading || isDeleting}
            paginationMode="server" // Enable server-side pagination
            totalRows={displayTotalRows}
            pageSize={pageSize}
            page={currentPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onClick={(e) => {
              // Track exercise access when actually clicked
              if (e.id) {
                exerciseCache.trackAccess(e.id);
              }
              navigate(`/home/exercises/exercise/${organizationId}/${e.id}`);
            }}
            onEdit={(e) => {
              // edit the exercise
              selectedExercise.current = e;
              setExerciseDialogOpen(true);
              events.logButtonClick({
                buttonName: "Edit Exercise",
                pageName: "Exercises",
              });
            }}
            onRestore={() => {
              // Restore functionality completely removed to eliminate Firebase costs
              console.log("Restore functionality has been removed");
            }}
            onDelete={async (exercise) => {
              console.log("🗑️ Delete exercise requested:", exercise.name);

              // Check if exercise is used in training plans (if this data is available)
              if (exercise.plans > 0) {
                confirm({
                  cancellationText: "",
                  title: "Cannot delete the exercise.",
                  description:
                    "This exercise is used in training plans. Please remove it from the plans before deleting.",
                }).catch(() => {
                  // User dismissed the message - no action needed
                });
                events.logButtonClick({
                  buttonName: "Attempted to Delete Exercise",
                  pageName: "Exercises",
                });
                return;
              }

              // For custom exercises, show appropriate confirmation message
              if (exercise.isCustom) {
                const confirmMessage = exercise.isGlobal
                  ? "Deleting this custom exercise cannot be undone. It will revert to the original configuration. Continue?"
                  : "Deleting this custom exercise cannot be undone. There is no default for this exercise. Continue?";

                confirm({
                  title: `Delete ${exercise.name}?`,
                  description: confirmMessage,
                })
                  .then(async () => {
                    try {
                      console.log(
                        "🗑️ Soft deleting exercise via BigQuery:",
                        exercise.id,
                      );
                      console.log("🔄 Delete mutation loading state BEFORE delete:", memoizedDeleteExercise.isLoading);
                      
                      setIsDeleting(true); // Show loading overlay
                      try {
                        await memoizedDeleteExercise.mutateAsync(exercise.id);
                      } finally {
                        setIsDeleting(false); // Hide loading overlay
                      }
                      
                      console.log("🔄 Delete mutation loading state AFTER delete:", memoizedDeleteExercise.isLoading);
                      console.log("✅ Exercise soft deleted successfully");

                      // Since we're using soft delete, the change is immediately visible
                      // so we can refresh the list right away
                      await refetch();
                      console.log(
                        "🔄 Exercise list refreshed after soft deletion",
                      );

                      // Handle edge case: if we deleted the last item on the current page,
                      // and the page becomes empty, go back to the previous page
                      if (exercises.length === 1 && currentPage > 0) {
                        console.log(
                          "🔄 Moving to previous page after deleting last item",
                        );
                        handlePageChange(currentPage - 1);
                      }

                      events.logButtonClick({
                        buttonName: "Delete Exercise",
                        pageName: "Exercises",
                      });
                    } catch (error) {
                      console.error("❌ Failed to delete exercise:", error);
                      // TODO: Show error message to user
                    }
                  })
                  .catch(() => {
                    console.log("❌ Exercise deletion cancelled by user");
                  });
              } else {
                // Should not happen as only custom exercises can be deleted
                console.warn("⚠️ Attempted to delete non-custom exercise");
              }
            }}
          />
        </Box>

        {exerciseDialogOpen && (
          <ExerciseDialog
            exercise={selectedExercise.current}
            open={exerciseDialogOpen}
            onDialogClosed={async (exercise) => {
              // Dialog now handles the mutation internally
              // This callback only receives the result for any additional processing
              if (exercise) {
                console.log("✅ Exercise operation completed:", exercise.name);

                // Clear any pending search timeouts to prevent interference
                if (searchTimeout.current) {
                  clearTimeout(searchTimeout.current);
                  console.log("🔄 Cleared pending search timeout");
                }
              }

              // Dialog closes after the API call completes
              setExerciseDialogOpen(false);
            }}
          />
        )}

        {/* Total count display only */}
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Total Exercises: {totalRows}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

// ExerciseDetail Component - Detail view of a specific exercise
const ExerciseDetail = () => {
  return <ExerciseDetails />;
};

// Main ExercisesPage Component with Routes
const ExercisesPage = () => {
  return (
    <Routes>
      <Route path="" element={<ExercisesList />} />
      <Route path="exercise/:orgId/:exerciseId" element={<ExerciseDetail />} />
    </Routes>
  );
};

export default ExercisesPage;
